/**
 * 全画面共通のレイアウト(モックアップv2.3準拠)。
 * **役割別メニュー(v3.5 §3)**: ログインした flow_role によって表示される画面自体が変わる。
 *   一般=申請のみ / 部門長=+承認・支払い+事業部予実 / 承認者=+承認・支払い+全社PL /
 *   経理=+承認・支払い+取引一覧・CF・CF月次推移・経費管理表・固定費マスタ・マスタ管理
 * データは同一DBで、承認システムを通った取引は自動的に各予実・CFへ反映される。
 * 月次/週次の切替は申請系画面には表示しない(モックv2.3)。
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { usePeriod } from './PeriodProvider';
import { appEnv, getProjectId } from '@core/lib/firebase';
import { toMonthLabel } from '@core/lib/date';
import { stageOf, subscribeMyUnread } from '@core/flow/notificationsRepo';
import { APP, APP_NAMES, appBaseUrl, boardAccessOf, boardHomePath, boardMenu, canUseBoard } from '@core/apps/appTarget';
import { FLOW_ROLE_LABELS } from '@core/lib/types';
import PasswordModal from '@core/auth/PasswordModal';
import MobileTabBar from './MobileTabBar';
import { useIsMobile } from '@core/lib/useIsMobile';
import { useStackedTables } from '@core/lib/stackedTables';
import { reconnectNow } from '@core/lib/firebaseReconnect';
import { useMasters } from '@core/masters/MastersProvider';
import { runRecurringHousekeeping } from '@core/recurring/autoRecurring';
import Modal from './Modal';

type NavItem = { to: string; label: string; visible: boolean; badge?: number };

/** 新しいバージョンの確認ポップアップで「あとで」を押してから、もう一度出すまでの時間 */
const UPDATE_PROMPT_SNOOZE_MS = 10 * 60 * 1000;

export const SCREEN_TITLES: Record<string, string> = {
  '/apply': '経費申請',
  '/report': '支払い済み申請',
  '/news': 'お知らせ',
  '/mylist': '申請一覧',
  '/todo': 'やること',
  '/approve': '承認',
  '/pay': '支払い待ち',
  '/queue': '支払キュー',
  '/invoices': '請求書・入金',
  '/receive': '受領済み',
  '/receive-wait': '受領待ち',
  '/acct-confirm': '支払い済み申請確認',
  '/confirm': '完了確認',
  '/transactions': '取引一覧',
  '/cf': '資金繰り(CF)',
  '/cf/matrix': 'CF月次推移',
  '/ledger': '経費管理表',
  '/recurring': '繰り返し支払い',
  '/pl': '全社PL',
  '/pl/accounts': '勘定科目別 予実',
  '/pl/balances': '売掛・買掛・未払残高',
  '/pl/honbu': '本部PL',
  '/pl/unso': '運送PL',
  '/routine': 'Routine の締め',
  '/pl/dept': '事業部予実',
  '/revenue': '売上・原価入力',
  '/budget': '予算入力',
  '/master': 'マスタ管理',
};

/** 月次セレクタを出さない画面(申請系。モックv2.3) */
const NO_PERIOD_PATHS = new Set(['/apply', '/report', '/news', '/mylist', '/todo', '/approve', '/pay', '/queue', '/receive', '/receive-wait', '/acct-confirm', '/confirm', '/master', '/no-access', '/routine']);

export default function AppLayout() {
  const { user, signOut, can, canConfirm, flowRole } = useAuth();
  const { month, setMonth, options } = usePeriod();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // info=自分の申請の進捗(申請一覧へ) / 操作依頼はページ別(承認/支払い/受領/完了確認)
  const [unread, setUnread] = useState({ info: 0, approve: 0, pay: 0, receive: 0, confirm: 0, acct: 0 });
  /** スマホのドロワーメニュー(2026-08-28・SC Calendar方式)。ルート遷移で自動的に閉じる */
  /** パスワード変更のポップアップ(2026-09-03 再設置) */
  const [pwOpen, setPwOpen] = useState(false);
  /** 本人がまだパスワードを決めていない(初期パスワードのまま)か */
  const mustSetPassword = !!user && !user.password_set_at;
  const [menuOpen, setMenuOpen] = useState(false);
  /** スマホは下部固定タブ(■17 第1弾・2026-08-31)。ドロワーは残す */
  const isMobile = useIsMobile();
  /** スマホでは全ての表を縦積みカードにする(2026-09-07 指示)。見出しをセルへ写す監視をここで1回だけ行う */
  useStackedTables();
  /** 繰り返し支払い(2026-09-07): 経理・承認者がアプリを開いたときに、当月〜翌々月分の「支払い待ち」を作り、
      支払日が近い分のお知らせを出す(サーバー側の自動実行が使えないための代替。設定でON/OFF) */
  const { fiscal } = useMasters();
  useEffect(() => {
    // 支払い待ちを作るのは Gate の仕事。Board(2026-10-23 別のアプリ・見るのが中心)では動かさない
    if (!user || APP === 'board' || !(canConfirm || can('master_admin'))) return;
    const run = () => void runRecurringHousekeeping({ userId: user.id, fiscalStartMonth: fiscal.start_month });
    run();
    // 開きっぱなしのタブでも翌日に前日分のお知らせが出るよう、30分ごと・タブに戻ったときにも動かす(実行は30分に1回まで)
    const timer = window.setInterval(run, 30 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, canConfirm, can, fiscal.start_month]);
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isBucho = flowRole === 'bucho';
  const isApprover = flowRole === 'approver';
  const isAccountingRole = flowRole === 'accounting';

  /**
   * 画面に戻ってきたとき・電波が戻ったときに、Firestoreをすぐつなぎ直す(2026-09-30)。
   * スマホは画面を消している間に通信が切れる。Firestore は再接続の間隔を最大60秒まで
   * 空けるため、戻ってすぐ申請や承認を押すと「送信中」のまま何十秒も待たされていた。
   * 10秒以上離れていたときだけ行う(画面を軽くのぞいただけでは何もしない)
   */
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt > 0 && Date.now() - hiddenAt > 10_000) void reconnectNow();
      hiddenAt = 0;
    };
    const onOnline = () => void reconnectNow();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // 新バージョン検知(2026-08-26 → 2026-09-10 強化): 30秒ごと・タブ復帰時・画面を移ったときに
  // 配信中のファイル名を確認し、変わっていたら「バージョン更新」を出す(Ctrl+Shift+R 不要)
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const current = document.querySelector<HTMLScriptElement>('script[src*="assets/index-"]')?.src ?? '';
    if (!current) return;
    let stopped = false;
    const check = async () => {
      try {
        // ブラウザにもCDNにも古い入口を返させない
        const html = await (await fetch(`/?_=${Date.now()}`, { cache: 'no-store' })).text();
        const m = html.match(/assets\/index-[^"]+\.js/);
        if (!stopped && m && !current.includes(m[0])) setUpdateAvailable(true);
      } catch {
        // オフライン等は無視して次回に任せる
      }
    };
    void check();
    const timer = setInterval(() => void check(), 30_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [pathname]);

  // 新しいバージョンの確認ポップアップ(2026-09-17 指示)。上部の青い帯は、申請の詳細などのポップアップを
  // 開いている間はその裏に隠れて気づけなかったため、ほかのポップアップより手前に出す。
  // 「あとで」(Esc・外側のクリックも同じ)を押したら閉じ、10分後にもう一度出す。帯とメニューの印はそのまま残す
  const [updatePromptOpen, setUpdatePromptOpen] = useState(false);
  const [updatePromptSnoozedUntil, setUpdatePromptSnoozedUntil] = useState(0);
  useEffect(() => {
    if (!updateAvailable || updatePromptOpen) return;
    const wait = updatePromptSnoozedUntil - Date.now();
    if (wait <= 0) {
      setUpdatePromptOpen(true);
      return;
    }
    const timer = window.setTimeout(() => setUpdatePromptOpen(true), wait);
    return () => window.clearTimeout(timer);
  }, [updateAvailable, updatePromptOpen, updatePromptSnoozedUntil]);

  function postponeUpdate() {
    setUpdatePromptOpen(false);
    setUpdatePromptSnoozedUntil(Date.now() + UPDATE_PROMPT_SNOOZE_MS);
  }

  /** 最新版に入れ替える。保存された古いファイルを消してから読み直す(2026-09-10) */
  async function updateToLatest() {
    try {
      const keys = (await caches?.keys?.()) ?? [];
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      // 保存領域が使えない環境は無視する
    }
    try {
      const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      // 未登録なら何もしない
    }
    window.location.reload();
  }

  // 未読バッジ: リアルタイム購読(2026-08-26)。ページを更新しなくても即時に増減する。
  // 既読になるのは該当画面で「詳細」を開いた時のみ
  useEffect(() => {
    // Board(2026-10-23 別のアプリ)では申請のお知らせを出さないため読まない
    if (!user || APP === 'board') return;
    const unsubscribe = subscribeMyUnread(user.id, (list) => {
      const count = (stage: 'approve' | 'pay' | 'receive' | 'confirm' | 'acct_confirm' | null) =>
        list.filter((n) => stageOf(n) === stage).length;
      setUnread({
        info: count(null),
        approve: count('approve'),
        pay: count('pay'),
        receive: count('receive'),
        confirm: count('confirm'),
        acct: count('acct_confirm'),
      });
    });
    return unsubscribe;
  }, [user]);

  /** やること(承認・支払い・受領・支払い済み申請確認・完了確認)の未処理合計 */
  const todoBadge = unread.approve + unread.pay + unread.receive + unread.confirm + unread.acct;

  // メニュー(ドロワー)に「やること」は出さない(2026-08-31 指示)。
  // やることは下部タブからのみ開く(/todo のルートは維持)
  const applyItems: NavItem[] = [
    { to: '/apply', label: '経費申請', visible: true },
    // 報告型(事後報告・承認不要。2026-08-26 会議→2026-08-27 改称)。全権限が使用可
    { to: '/report', label: '支払い済み申請', visible: true },
    // 請求書申請は廃止(2026-08-23)。後継の報酬支払申請は仕様確定後に追加する
    { to: '/mylist', label: '申請一覧', visible: true },
    // お知らせ欄(通知センター・2026-08-26 会議)。バッジはステータス更新のお知らせの未読数
    { to: '/news', label: 'お知らせ', visible: true, badge: unread.info },
    // ステータス別ページ(2026-08-26 分割)。バッジは各ページ宛ての未読操作依頼
    { to: '/approve', label: '承認', visible: isBucho || isApprover, badge: unread.approve },
    // 「支払い」は行為の名前で何の画面か分かりにくかったため、ほかの2つ(受領待ち・受領済み)と同じく状態の名前に(2026-10-16 指示)
    { to: '/pay', label: '支払い待ち', visible: true, badge: unread.pay },
    // 受領待ち=まだ受け取っていないお金 / 受領済み=受け取ったお金(履歴)。受け取りの記録の作業は受領待ちにある(2026-10-15)。
    // 並びはお金の流れの順(支払い→受領待ち→受領済み)。「受領」だけだと作業の名前に見えるため「受領済み」に(2026-10-15 指示)
    { to: '/receive-wait', label: '受領待ち', visible: true, badge: unread.receive },
    { to: '/receive', label: '受領済み', visible: true },
    // 支払い済み申請確認(旧: 経理確認。2026-08-28 改称・フローは変更なし)
    { to: '/acct-confirm', label: '支払い済み申請確認', visible: isAccountingRole || can('master_admin'), badge: unread.acct },
    { to: '/confirm', label: '完了確認', visible: isApprover, badge: unread.confirm },
    // 支払キュー(2026-10-23 段階2): まだ支払っていないもの全体を期日順に見る一覧(見るだけ)。経理・承認者・管理者
    { to: '/queue', label: '支払キュー', visible: isAccountingRole || isApprover || can('master_admin') },
    // 請求書・入金(2026-10-23 段階3 収入側)。経理・承認者・管理者
    { to: '/invoices', label: '請求書・入金', visible: isAccountingRole || isApprover || can('master_admin') },
    // Routine の締め(2026-10-23): 運送の締め(①請求・②下請け・③ドライバー)の確定が届く画面。経理・承認者・管理者
    { to: '/routine', label: 'Routine の締め', visible: isAccountingRole || isApprover || can('master_admin') },
  ];
  // 予実管理系のメニュー項目は撤去(■16追記・2026-08-28: 権限者にも表示しない)。
  // ルート自体は App.tsx に残っており、直接URLでのみ到達できる。
  // マスタ管理のみ、一旦この経費申請システムに置く(2026-08-28 発注者指示。
  // 今後作成する全社PL側で管理する可能性あり)
  const adminItems: NavItem[] = [
    // 繰り返し支払い(2026-09-07 指示): 家賃・リースなどの定期支払いの設定。経理と承認者。
    // 同日指示で「一旦後回し」のため、画面が固まるまでメニューには出さない(URL /recurring では開ける)
    { to: '/recurring', label: '繰り返し支払い', visible: canConfirm || can('master_admin') },
    { to: '/master', label: 'マスタ管理', visible: can('master_admin') },
  ];

  // Gate と Board を別のアプリに(2026-10-23)。Board のメニューは Board の画面だけ(入室条件は App.tsx と同じ)
  const boardAccess = boardAccessOf({ can, canConfirm, flowRole, user });
  const boardItems: NavItem[] = boardMenu(boardAccess);
  const gateUrl = appBaseUrl('gate', import.meta.env);
  const boardUrl = appBaseUrl('board', import.meta.env);
  /** 相手のアプリへのリンク(URL が設定されていて、使える人だけ) */
  const otherApp =
    APP === 'board'
      ? gateUrl && { href: gateUrl, label: 'Nexus Gate を開く' }
      : boardUrl && canUseBoard(boardAccess) && { href: boardUrl, label: 'Nexus Board を開く' };

  const title = SCREEN_TITLES[pathname] ?? APP_NAMES[APP];
  const showPeriod = !NO_PERIOD_PATHS.has(pathname);
  /** 下部タブの一次画面(戻り先がない)以外で「戻る」を出す(2026-08-31 指示) */
  // Board はログイン後に最初に開く画面(戻り先がない)で出さない
  const showBack =
    APP === 'board' ? pathname !== (boardHomePath(boardAccess) ?? '/no-access') : !['/apply', '/mylist', '/todo', '/news'].includes(pathname);

  const renderItems = (items: NavItem[]) =>
    items
      .filter((i) => i.visible)
      .map((i) => (
        // end: 「全社PL(/pl)」が「勘定科目別(/pl/accounts)」を開いているときに光らないように(2026-10-23)
        <NavLink key={i.to} to={i.to} end className={({ isActive }) => `side-item${isActive ? ' on' : ''}`}>
          {i.label}
          {(i.badge ?? 0) > 0 && (
            <span className="badge b-pend" style={{ marginLeft: 8 }}>
              {i.badge}
            </span>
          )}
        </NavLink>
      ));

  return (
    <div className={`app${menuOpen ? ' menu-open' : ''}`}>
      <aside>
        <div className="side-brand">
          <div className="brand">SMART CONTROL / CFO</div>
          <div className="side-title">
            <img className="side-logo" src="/brand/mark.png?v=5" alt="" />
            {APP_NAMES[APP]}
          </div>
          {/* いま動いているバージョンと更新ボタン(2026-09-10 指示。ロゴの下に出す) */}
          <div className="side-version">バージョン {__BUILD_ID__}{__COMMIT_ID__ && ` · ${__COMMIT_ID__}`}</div>
          <button
            className={`side-update${updateAvailable ? ' on' : ''}`}
            onClick={() => void updateToLatest()}
            title={updateAvailable ? '新しいバージョンがあります。押すと最新にします' : '最新かどうかを確かめて画面を読み直します'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            {updateAvailable ? '新しいバージョンに更新' : 'バージョン更新'}
          </button>
        </div>

        {/* ■16(2026-08-28 会議→同日追記): 経費申請システムとして運用するため、
            メニューは経費関連のみ。予実管理系(PL・CF・取引一覧・経費管理表・売上原価・
            固定費・予算・マスタ)は**権限者にも表示しない**(ページ自体は直接URLで到達可能。
            予実管理側の導線は別途整備する方針) */}
        {APP === 'board' ? (
          <>
            {boardItems.some((i) => i.visible) && <div className="side-sec">予実・PL</div>}
            {renderItems(boardItems)}
          </>
        ) : (
          <>
            <div className="side-sec">経費申請</div>
            {renderItems(applyItems)}

            {adminItems.some((i) => i.visible) && <div className="side-sec">管理</div>}
            {renderItems(adminItems)}
          </>
        )}
        {otherApp && (
          // 相手のアプリは新しいタブで開く(2026-10-23 発注者指示)
          <a className="side-item side-other-app" href={otherApp.href} target="_blank" rel="noopener noreferrer">
            {otherApp.label} ↗
          </a>
        )}

        <div className="side-foot">
          {/* パスワード変更(2026-09-03 再設置): 本人がいつでも変更できる */}
          <button className="side-guide" style={{ background: 'none', border: 'none', width: '100%' }} onClick={() => setPwOpen(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            パスワード変更
          </button>
          {/* 使い方ガイド(2026-08-31): 実画面つきのマニュアル。別タブで開く */}
          <a className="side-guide" href="/manual" target="_blank" rel="noopener noreferrer">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            使い方ガイド
          </a>
          <button className="side-signout" onClick={() => void signOut()}>
            ログアウト
          </button>
        </div>
      </aside>
      {/* ドロワーの背景(タップで閉じる) */}
      <div className="side-overlay" onClick={() => setMenuOpen(false)} />

      <main>
        {/* 新バージョンの案内(2026-08-26)。クリックで再読込して最新化 */}
        {updateAvailable && (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 18px',
              background: '#024BD5',
              color: '#fff',
              fontSize: 13.5,
            }}
          >
            {/* 文言は最小限に(2026-08-28 指示。スマホで改行だらけになり読みにくいため) */}
            <b>新しいバージョンがあります。</b>
            <button
              onClick={() => void updateToLatest()}
              style={{
                marginLeft: 'auto',
                padding: '6px 18px',
                borderRadius: 6,
                border: 'none',
                background: '#fff',
                color: '#024BD5',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              今すぐ更新
            </button>
          </div>
        )}
        {/* テスト環境の帯は、手元の開発サーバー(npm run dev)と、確認用テスト環境(VITE_ENV_BANNER=true でビルド)でだけ表示する。
            2026-08-31 本番運用開始により、本番の配信アプリでは出さない(本番のビルドには VITE_ENV_BANNER を設定しない)。
            確認用テスト環境の名前は VITE_ENV_BANNER_LABEL で出す(2026-10-22) */}
        {((import.meta.env.DEV && appEnv === 'dev') || import.meta.env.VITE_ENV_BANNER === 'true') && (
          <div className="envband">
            <span>テスト環境({import.meta.env.VITE_ENV_BANNER_LABEL || getProjectId()})に接続しています</span>{' '}
            <span>— 本番データではありません</span>
          </div>
        )}

        <div className="topbar">
          {/* スマホのみ表示のメニューボタン(ドロワーを開く) */}
          <button className="menu-btn" aria-label="メニュー" onClick={() => setMenuOpen(true)}>
            ☰
          </button>
          {/* 戻る(2026-08-31 指示)。ホーム画面から起動したPWAにはブラウザの戻るが
              無いため、画面内に置く。タブの一次画面(申請・一覧・やること・お知らせ)
              では出さない(戻り先がないため) */}
          {showBack && (
            <button
              className="back-btn"
              aria-label="前の画面に戻る"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(APP === 'board' ? '/' : '/apply'))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              戻る
            </button>
          )}
          <h2>{title}</h2>
          {showPeriod && (
            <select aria-label="対象月" value={month} onChange={(e) => setMonth(e.target.value)}>
              {options.map((m) => (
                <option key={m} value={m}>
                  {toMonthLabel(m)}
                </option>
              ))}
            </select>
          )}
          {/* 更新ボタン(2026-08-28 指示): 自分でいつでも最新化できるように。ベルと同じ線画 */}
          <button
            aria-label="更新"
            title="最新のバージョンに更新する"
            onClick={() => void updateToLatest()}
            style={{
              position: 'relative',
              background: 'none',
              border: 'none',
              padding: 0,
              lineHeight: 0,
              flexShrink: 0,
              color: updateAvailable ? '#024BD5' : 'var(--ink)',
              cursor: 'pointer',
            }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
            {/* 新しいバージョンがあるときは印を出す(スマホでも気づけるように。2026-09-10) */}
            {updateAvailable && (
              <span
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: '#024BD5',
                  border: '1.5px solid #fff',
                }}
              />
            )}
          </button>
          {/* お知らせベル(2026-08-28): どの画面からも即お知らせへ。未読数を赤バッジで表示。Board には出さない(2026-10-23) */}
          {APP === 'gate' && (
          <NavLink
            to="/news"
            aria-label="お知らせ"
            title="お知らせ"
            // 未読バッジが名前と重ならないよう右に余白をとる(2026-08-31 指示)
            className="topbar-bell"
            style={{ position: 'relative', textDecoration: 'none', lineHeight: 0, flexShrink: 0, color: 'var(--ink)' }}
          >
            {/* 線画のベルアイコン(2026-08-28 指示。絵文字🔔から変更) */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unread.info > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -10,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 9,
                  background: 'var(--red)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {unread.info > 99 ? '99+' : unread.info}
              </span>
            )}
          </NavLink>
          )}
          <span className="user">
            {user?.display_name}
            {/* 役割はスマホでは省く(画面名の場所を確保するため。2026-09-06) */}
            <span className="user-role"> ・{FLOW_ROLE_LABELS[flowRole]}</span>
          </span>
        </div>

        <div className={`content${isMobile && APP === 'gate' ? ' has-tabbar' : ''}`}>
          <Outlet />
        </div>

        {/* スマホの下部固定タブ(■17 第1弾・2026-08-31)。日常操作はここで完結する */}
        {isMobile && APP === 'gate' && <MobileTabBar todoCount={todoBadge} newsCount={unread.info} payCount={unread.pay} receiveCount={unread.receive} />}

        {/* 新しいバージョンの確認(2026-09-17 指示)。後から開くため、開いている詳細などのポップアップより手前に出る */}
        {updatePromptOpen && (
          <Modal onClose={postponeUpdate} width="min(420px, 92vw)" showClose={false} compact>
            <h3 style={{ margin: '4px 0 10px' }}>新しいバージョンがあります</h3>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.8 }}>入力中の内容がある場合は、保存してから更新してください</p>
            <div className="modal-actions">
              <button className="btn-green" onClick={() => void updateToLatest()}>
                今すぐ更新
              </button>
              <button className="btn-line" onClick={postponeUpdate}>
                あとで
              </button>
            </div>
          </Modal>
        )}

        {/* 初回ログイン(本人がまだパスワードを決めていない)は設定するまで閉じられない(2026-09-08 指示) */}
        {(pwOpen || mustSetPassword) && (
          <PasswordModal
            forced={mustSetPassword}
            onClose={() => {
              setPwOpen(false);
              // 設定済みの印はモーダル側(markPasswordSet)で即反映する。ここで読み直すと、印の更新が遅れた場合に再表示されてしまう
            }}
          />
        )}
      </main>
    </div>
  );
}
